import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardBody, Chip, Image, Input, Skeleton } from '@nextui-org/react';
import {
  Calendar,
  MessagesSquare,
  Newspaper,
  Search,
  SearchX,
  Store,
  Target,
  Trophy,
  Users,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import searchService, {
  GroupedSearchResults,
  SearchResult,
  SearchResultType,
  SEARCH_TYPE_LABELS,
  SEARCH_TYPE_ORDER,
} from '../services/searchService';

const ACCENT = '#3fced1';
const DEBOUNCE_MS = 400;

type FilterValue = 'all' | SearchResultType;

const TYPE_ICONS: Record<
  SearchResultType,
  React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>
> = {
  tournament: Trophy,
  club: Users,
  pub: Target,
  shop: Store,
  series: Calendar,
  news: Newspaper,
  forum: MessagesSquare,
};

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState<string>(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState<string>(initialQuery.trim());
  const [results, setResults] = useState<GroupedSearchResults>(searchService.emptyResults());
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterValue>('all');

  // Schutz gegen Race Conditions bei parallelen Requests
  const requestIdRef = useRef(0);

  // Debounce: query -> debouncedQuery (400ms)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  // URL synchron halten (?q=...)
  useEffect(() => {
    const currentQ = searchParams.get('q') || '';
    if (debouncedQuery === currentQ) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedQuery) {
      next.set('q', debouncedQuery);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Auf URL-Änderungen von außen reagieren (z. B. Header-Suche navigiert erneut)
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    setQuery((prev) => (prev.trim() === urlQuery.trim() ? prev : urlQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const runSearch = useCallback(async (term: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const grouped = await searchService.searchAll(term);
      if (requestId !== requestIdRef.current) return; // veralteter Request
      setResults(grouped);
      setHasSearched(true);
    } catch (error) {
      console.error('Globale Suche fehlgeschlagen:', error);
      if (requestId === requestIdRef.current) {
        setResults(searchService.emptyResults());
        setHasSearched(true);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Suche ausführen, sobald der debounced Begriff lang genug ist
  useEffect(() => {
    if (debouncedQuery.length >= searchService.minQueryLength) {
      runSearch(debouncedQuery);
    } else {
      requestIdRef.current += 1; // laufende Requests verwerfen
      setLoading(false);
      setResults(searchService.emptyResults());
      setHasSearched(false);
    }
  }, [debouncedQuery, runSearch]);

  const totalCount = useMemo(() => searchService.countResults(results), [results]);

  const visibleTypes = useMemo(
    () =>
      SEARCH_TYPE_ORDER.filter(
        (type) => activeFilter === 'all' || activeFilter === type,
      ),
    [activeFilter],
  );

  const queryTooShort =
    debouncedQuery.length > 0 && debouncedQuery.length < searchService.minQueryLength;
  const showEmptyState =
    !loading &&
    hasSearched &&
    debouncedQuery.length >= searchService.minQueryLength &&
    totalCount === 0;
  const showIdleHint = !loading && debouncedQuery.length === 0;

  const handleResultClick = (result: SearchResult) => {
    if (result.link) navigate(result.link);
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>{debouncedQuery ? `Suche: ${debouncedQuery} - DartCloud` : 'Suche - DartCloud'}</title>
        <meta
          name="description"
          content="Durchsuche Turniere, Vereine, Spielstätten, Shops, Turnierserien, News und das Forum auf DartCloud."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Search style={{ color: ACCENT }} size={40} />
            Suche
          </h1>
          <p className="text-gray-400">
            Finde Turniere, Vereine, Spielstätten, Shops, Turnierserien, News und Forum-Beiträge
          </p>
        </div>

        {/* Großes Suchfeld */}
        <div className="mb-6">
          <Input
            autoFocus
            size="lg"
            placeholder="Wonach suchst du? (z. B. Turnier, Verein, Stadt ...)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            isClearable
            startContent={<Search size={22} className="text-gray-400" />}
            classNames={{
              input: 'bg-zinc-900 text-base',
              inputWrapper: 'bg-zinc-900 border-zinc-800 h-14',
            }}
          />
          {queryTooShort && (
            <p className="text-sm text-gray-500 mt-2">
              Bitte mindestens {searchService.minQueryLength} Zeichen eingeben.
            </p>
          )}
        </div>

        {/* Filter-Chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Chip
            className="cursor-pointer"
            variant={activeFilter === 'all' ? 'solid' : 'bordered'}
            style={
              activeFilter === 'all'
                ? { backgroundColor: ACCENT, color: '#09090b' }
                : { borderColor: '#3f3f46', color: '#a1a1aa' }
            }
            onClick={() => setActiveFilter('all')}
          >
            Alle{hasSearched ? ` (${totalCount})` : ''}
          </Chip>
          {SEARCH_TYPE_ORDER.map((type) => {
            const count = results[type]?.length || 0;
            const isActive = activeFilter === type;
            return (
              <Chip
                key={type}
                className="cursor-pointer"
                variant={isActive ? 'solid' : 'bordered'}
                style={
                  isActive
                    ? { backgroundColor: ACCENT, color: '#09090b' }
                    : { borderColor: '#3f3f46', color: '#a1a1aa' }
                }
                onClick={() => setActiveFilter(isActive ? 'all' : type)}
              >
                {SEARCH_TYPE_LABELS[type]}
                {hasSearched ? ` (${count})` : ''}
              </Chip>
            );
          })}
        </div>

        {/* Loading-Skeleton */}
        {loading && (
          <div className="space-y-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Card key={i} className="bg-zinc-900 border-zinc-800">
                <CardBody className="flex flex-row items-center gap-4 p-4">
                  <Skeleton className="rounded-lg bg-zinc-800">
                    <div className="w-16 h-16 rounded-lg" />
                  </Skeleton>
                  <div className="flex-1 space-y-2">
                    <Skeleton className="w-3/5 rounded-lg bg-zinc-800">
                      <div className="h-4 rounded-lg" />
                    </Skeleton>
                    <Skeleton className="w-2/5 rounded-lg bg-zinc-800">
                      <div className="h-3 rounded-lg" />
                    </Skeleton>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {/* Hinweis bei leerem Suchfeld */}
        {showIdleHint && (
          <div className="text-center py-16">
            <Search size={64} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Starte deine Suche
            </h3>
            <p className="text-gray-500">
              Gib oben einen Suchbegriff ein, um Turniere, Vereine, Spielstätten,
              Shops, Turnierserien, News und Forum-Beiträge zu finden.
            </p>
          </div>
        )}

        {/* Empty-State */}
        {showEmptyState && (
          <div className="text-center py-16">
            <SearchX size={64} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Keine Ergebnisse für „{debouncedQuery}“
            </h3>
            <p className="text-gray-500">
              Versuche einen anderen Suchbegriff oder prüfe die Schreibweise.
            </p>
          </div>
        )}

        {/* Ergebnisse gruppiert nach Typ */}
        {!loading && hasSearched && totalCount > 0 && (
          <div className="space-y-10">
            {visibleTypes.map((type) => {
              const items = results[type] || [];
              if (items.length === 0) return null;
              const TypeIcon = TYPE_ICONS[type];
              return (
                <section key={type}>
                  <div className="flex items-center gap-2 mb-4">
                    <TypeIcon size={22} className="shrink-0" style={{ color: ACCENT } as React.CSSProperties} />
                    <h2 className="text-xl font-semibold text-white">
                      {SEARCH_TYPE_LABELS[type]}
                    </h2>
                    <Chip size="sm" variant="flat" className="bg-zinc-800 text-gray-400">
                      {items.length}
                    </Chip>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((result) => (
                      <Card
                        key={`${result.type}-${result.id}`}
                        className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 transition-colors"
                        isPressable={!!result.link}
                        onPress={() => handleResultClick(result)}
                      >
                        <CardBody className="flex flex-row items-center gap-4 p-4">
                          {result.imageUrl ? (
                            <Image
                              removeWrapper
                              alt={result.title}
                              src={result.imageUrl}
                              fallbackSrc="/icon.png"
                              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                              <TypeIcon size={24} className="text-gray-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-white line-clamp-1">
                              {result.title}
                            </h3>
                            {result.subtitle && (
                              <p className="text-sm text-gray-400 line-clamp-2">
                                {result.subtitle}
                              </p>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </section>
              );
            })}

            {/* Aktiver Filter ohne Treffer */}
            {activeFilter !== 'all' && (results[activeFilter]?.length || 0) === 0 && (
              <div className="text-center py-16">
                <SearchX size={64} className="mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">
                  Keine {SEARCH_TYPE_LABELS[activeFilter]} für „{debouncedQuery}“
                </h3>
                <p className="text-gray-500">
                  Wähle einen anderen Filter oder ändere den Suchbegriff.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
